import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import ChatNotificationProvider from "./components/ChatNotificationProvider";

// Lazy load non-critical routes to reduce initial bundle size
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const OwnerLogin = lazy(() => import("./pages/OwnerLogin"));
const OwnerDashboard = lazy(() => import("./pages/OwnerDashboard"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Feed = lazy(() => import("./pages/Feed"));
const Profile = lazy(() => import("./pages/Profile"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const Booking = lazy(() => import("./pages/Booking"));
const Chat = lazy(() => import("./pages/Chat"));
const Discover = lazy(() => import("./pages/Discover"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Pricing = lazy(() => import("./pages/Pricing"));
const MembershipCheckout = lazy(() => import("./pages/MembershipCheckout"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const FinancialPortal = lazy(() => import("./pages/FinancialPortal"));
const PartnerLogin = lazy(() => import("./pages/PartnerLogin"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const TrainerLogin = lazy(() => import("./pages/TrainerLogin"));
const TrainerDashboard = lazy(() => import("./pages/TrainerDashboard"));
const CRMPortal = lazy(() => import("./pages/CRMPortal"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ChatNotificationProvider />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/feed" element={<Feed />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/user/:userId" element={<UserProfile />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/discover" element={<Discover />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/membership-checkout/:packageId" element={<MembershipCheckout />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute requiredRole="admin" redirectTo="/admin/login">
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/financial" 
                element={
                  <ProtectedRoute requiredRole="admin" redirectTo="/admin/login">
                    <FinancialPortal />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/admin/crm" 
                element={
                  <ProtectedRoute requiredRole={["admin", "owner", "trainer"]} redirectTo="/login">
                    <CRMPortal />
                  </ProtectedRoute>
                }
              />
              <Route path="/owner/login" element={<OwnerLogin />} />
              <Route path="/owner-login" element={<OwnerLogin />} />
              <Route 
                path="/owner" 
                element={
                  <ProtectedRoute requiredRole="owner" redirectTo="/owner/login">
                    <OwnerDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route path="/partner/login" element={<PartnerLogin />} />
              <Route path="/partner-login" element={<PartnerLogin />} />
              <Route 
                path="/partner" 
                element={
                  <ProtectedRoute requiredRole="partner" redirectTo="/partner/login">
                    <PartnerDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route path="/trainer/login" element={<TrainerLogin />} />
              <Route path="/trainer-login" element={<TrainerLogin />} />
              <Route 
                path="/trainer" 
                element={
                  <ProtectedRoute requiredRole="trainer" redirectTo="/trainer/login">
                    <TrainerDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
